import {
  Box,
  Flex,
  Heading,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useColorModeValue,
  HStack,
  IconButton,
  Stack,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import {
  Table,
  TableHead,
  TableHeaderSortCell,
  TableSortDirection,
  TableBody,
  TableRow,
  TableCell,
  useNexus,
  BodyText,
  Button,
} from '@spotoninc/nexus-react';
import {
  MoreVerticalIcon,
  EditIcon,
  DeleteIcon,
  SuccessIcon,
  DangerIcon,
  AddIcon,
} from '@spotoninc/nexus-web-assets';
import React, { useState, useEffect } from 'react';
import { fetchWithAuth } from '../../utils/apiHelpers';
import AddOrEditUserModal from './addOrEditUserModal';
import DeactivateUserDialog from './deactivateUserDialog';
import { trackUserAdded } from '../../utils/analytics/integrationTracking';
const ManageUsers = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [privileges, setPrivileges] = useState([]);
  const [exclusions, setExclusions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeactivateDialogOpen, setIsDeactivateDialogOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: TableSortDirection.ascending,
  });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    theme: { tokens },
  } = useNexus();

  const fetchUserData = async () => {
    setIsLoading(true);
    try {
      const response = await fetchWithAuth('/api/fetch_auth_data/', {
        method: 'GET',
      });
      const data = await response.json();
      setUsers(data.users || []);
      setRoles(data.roles || []);
      setPrivileges(data.priviledges || []);
      setExclusions(data.exclusions || []);
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  const handleAddUser = async formData => {
    const formBody = new FormData();
    formBody.append('firstName', formData.firstName);
    formBody.append('lastName', formData.lastName);
    formBody.append('userEmail', formData.email);
    formBody.append('role_id', formData.role);
    formBody.append('priviledge_id', formData.privilege);
    // Only append exclusion_id if it's not empty
    if (formData.exclusion !== '') {
      formBody.append('exclusion_id', formData.exclusion);
    }
    formBody.append('pass', formData.password);
    formBody.append('changePass', formData.changePassword);
    formBody.append('user_id', selectedUser?.user_id_id || 'newUser');

    try {
      const response = await fetchWithAuth('/api/add_user/', {
        method: 'POST',
        body: formBody,
      });
      const data = await response.json();
      if (data.registration_status !== 'passed') {
        alert(data.registration_status);
      } else {
        // Track new user added event (only for new users, not edits)
        if (!selectedUser) {
          trackUserAdded({
            userEmail: formData.email,
            firstName: formData.firstName,
            lastName: formData.lastName,
            roleId: formData.role,
            privilegeId: formData.privilege,
          });
        }

        onClose();
        fetchUserData();
      }
    } catch (error) {
      console.error('Error adding user:', error);
    }
  };

  const handleDeactivateUser = async userId => {
    const formBody = new FormData();
    formBody.append('deactivate', 'true');
    formBody.append('user_id', userId);

    try {
      const response = await fetchWithAuth('/api/add_user/', {
        method: 'POST',
        body: formBody,
      });
      const data = await response.json();
      if (data.registration_status !== 'passed') {
        alert(data.registration_status);
      } else {
        fetchUserData();
      }
    } catch (error) {
      console.error('Error deactivating user:', error);
    }
  };

  const handleEditUser = user => {
    setSelectedUser(user);
    onOpen();
  };

  const handleAddNewUser = () => {
    setSelectedUser(null);
    onOpen();
  };

  const handleSort = (key, newDirection) => {
    setSortConfig({ key, direction: newDirection });
  };

  const sortData = data => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle string columns with case-insensitive alphabetical sorting
      if (sortConfig.key === 'first_name' || sortConfig.key === 'last_name') {
        // Convert to lowercase for case-insensitive comparison
        aValue = aValue ? aValue.toLowerCase() : '';
        bValue = bValue ? bValue.toLowerCase() : '';
      }
      // Special handling for status column (boolean to number conversion)
      else if (sortConfig.key === 'active') {
        aValue = a.active ? 1 : 0;
        bValue = b.active ? 1 : 0;
      }

      if (aValue < bValue) {
        return sortConfig.direction === TableSortDirection.ascending ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === TableSortDirection.ascending ? 1 : -1;
      }
      return 0;
    });
  };

  const sortedUsers = sortData(users);
  return (
    <Box p={6} pt={0}>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading
          size="lg"
          fontFamily={'var(--font-family-sans) !important'}
          fontWeight={600}
          color={'var(--semantic-color-text-neutral-strongest-none)'}
        >
          Manage Users
        </Heading>
        <Button
          leadingIcon={AddIcon}
          colorScheme="blue"
          onClick={handleAddNewUser}
        >
          Add User
        </Button>
      </Flex>

      <Box bg={useColorModeValue('white', 'gray.800')} overflow="hidden">
        <Table fontSize={tokens.fontSize100} fontWeight={600}>
          <TableHead>
            <TableRow variant="filled">
              <TableHeaderSortCell
                onSort={direction => handleSort('first_name', direction)}
                direction={
                  sortConfig.key === 'first_name'
                    ? sortConfig.direction
                    : undefined
                }
              >
                First Name
              </TableHeaderSortCell>
              <TableHeaderSortCell
                onSort={direction => handleSort('last_name', direction)}
                direction={
                  sortConfig.key === 'last_name'
                    ? sortConfig.direction
                    : undefined
                }
              >
                Last Name
              </TableHeaderSortCell>
              <TableHeaderSortCell
                onSort={direction => handleSort('active', direction)}
                direction={
                  sortConfig.key === 'active' ? sortConfig.direction : undefined
                }
              >
                Status
              </TableHeaderSortCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedUsers.map(user => (
              <TableRow key={user.user_id_id} variant="bottom-divider">
                <TableCell>
                  <BodyText>{user.first_name}</BodyText>
                </TableCell>
                <TableCell>
                  <BodyText>{user.last_name}</BodyText>
                </TableCell>
                <TableCell>
                  <HStack justify="space-between">
                    {user.active ? (
                      <Tooltip
                        label="Active user"
                        placement="top"
                        hasArrow
                        bg="gray.700"
                        color="white"
                        borderRadius="md"
                        px={4}
                        py={3}
                      >
                        <Stack direction="row" align="center" spacing={1}>
                          <SuccessIcon
                            width="18px"
                            height="18px"
                            color={tokens.semanticColorIconSuccessStrongNone}
                          />
                        </Stack>
                      </Tooltip>
                    ) : (
                      <Tooltip
                        label="User deactivated"
                        placement="top"
                        hasArrow
                        bg="gray.700"
                        color="white"
                        borderRadius="md"
                        px={4}
                        py={3}
                      >
                        <Stack direction="row" align="center" spacing={1}>
                          <DangerIcon
                            width="18px"
                            height="18px"
                            color={tokens.semanticColorIconDangerStrongNone}
                          />
                        </Stack>
                      </Tooltip>
                    )}
                    <Menu>
                      <MenuButton
                        as={IconButton}
                        icon={<MoreVerticalIcon />}
                        variant="ghost"
                        borderRadius="full"
                      />
                      <MenuList borderRadius="md" bg="gray.100">
                        <MenuItem
                          height="48px"
                          bg="gray.100"
                          icon={<Icon as={EditIcon} w="24px" h="24px" />}
                          onClick={() => handleEditUser(user)}
                          _hover={{ bg: '#D3E5FF' }}
                          borderRadius="md"
                        >
                          Edit
                        </MenuItem>
                        <MenuItem
                          height="48px"
                          bg="gray.100"
                          icon={
                            user.active ? (
                              <Icon as={DeleteIcon} w="24px" h="24px" />
                            ) : (
                              <Icon as={SuccessIcon} w="24px" h="24px" />
                            )
                          }
                          onClick={() => {
                            if (!user.active) {
                              handleDeactivateUser(user.user_id_id);
                            } else {
                              setSelectedUser(user);
                              setIsDeactivateDialogOpen(true);
                            }
                          }}
                          _hover={{ bg: '#D3E5FF' }}
                          borderRadius="md"
                        >
                          {user.active ? 'Deactivate' : 'Activate'}
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </HStack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <AddOrEditUserModal
        isOpen={isOpen}
        onClose={onClose}
        onSubmit={handleAddUser}
        user={selectedUser}
        roles={roles}
        privileges={privileges}
        exclusions={exclusions}
        isNewUser={selectedUser === null}
      />
      <DeactivateUserDialog
        isOpen={isDeactivateDialogOpen}
        onClose={() => setIsDeactivateDialogOpen(false)}
        onSubmit={() => handleDeactivateUser(selectedUser.user_id_id)}
      />
    </Box>
  );
};

export default ManageUsers;
